/**
 * Form primitives (ADR-061) — replaces `TextField` (173 imports),
 * `MenuItem` (98), `Checkbox` (38), `FormControlLabel` (35), `Switch`
 * and `Radio`.
 *
 * ONE FIELD SHELL, MANY CONTROLS. `Field` owns the label, the required
 * marker, the error message and the description; the controls own only
 * their own behaviour. That is what stops a select and a text input on
 * the same row from disagreeing about label size or error placement —
 * the defect the registration screen was rebuilt to fix.
 *
 * ERRORS ARE ANNOUNCED, NOT JUST COLOURED. Every error renders in a
 * `role="alert"` node and is wired to its control through
 * `aria-describedby` + `aria-invalid`. A red border alone is invisible
 * to a screen reader and to a red-green colour-blind operator, which is
 * WCAG 1.4.1 and 3.3.1.
 *
 * LABELS ARE REAL `<label htmlFor>`. Not a styled span — clicking the
 * label must focus the control, and a placeholder is never a label.
 */
import { createContext, forwardRef, useContext, useId, type InputHTMLAttributes, type JSX, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';
import { cn } from '@utils/cn';
import { Icon } from './icon';

interface FieldContextValue {
  controlId: string;
  describedBy?: string;
  invalid: boolean;
}
const FieldContext = createContext<FieldContextValue | null>(null);

/**
 * Shared control chrome. `h-9` (36px) is the dense default; the registration
 * grid overrides to 40px through its own cell styling.
 *
 * `text-sm` (14px), not the 12px this used to carry: the desk reads these
 * across a counter from a chair, and 12px value text was the substance of the
 * "can't see it" complaints. Placeholders moved off `text-faint` for the same
 * reason as the labels below: `tw:placeholder:text-nx-text-faint` is
 * 3.68:1 on white, which is not readable text.
 */
export const CONTROL_BASE =
  'nx-focusable tw:h-9 tw:w-full tw:rounded-md tw:border tw:bg-nx-surface tw:px-2 tw:text-sm tw:text-nx-text tw:transition-colors tw:duration-200 tw:placeholder:text-nx-text-muted tw:disabled:cursor-not-allowed tw:disabled:bg-nx-surface-sunken tw:disabled:text-nx-text-muted';

function controlClass(invalid: boolean, className?: string): string {
  return cn(CONTROL_BASE, invalid ? 'tw:border-nx-error' : 'tw:border-nx-border tw:hover:border-nx-border-strong', className);
}

export interface FieldProps {
  label: string;
  required?: boolean;
  /** Shown under the control when there is no error. */
  description?: string;
  /** Presence of this makes the field invalid and announces the message. */
  error?: string;
  children: ReactNode;
  className?: string;
  /** Hides the visible label but keeps it for assistive tech — only for a control whose purpose is obvious from an adjacent heading. */
  labelHidden?: boolean;
  /**
   * Suppresses the DESCRIPTION line on a dense grid, where per-field help
   * text on forty fields is noise rather than help.
   *
   * Errors are deliberately NOT suppressed by this. They were, when the
   * registration workstation listed them in a side panel instead — but
   * that panel has been removed, and an error the operator cannot see
   * next to the field it belongs to is worse than a row that grows by
   * one line. Cells are top-aligned, so an error under one field pushes
   * the following row down without disturbing column alignment.
   */
  hideMessage?: boolean;
}

export function Field({ label, required, description, error, children, className, labelHidden, hideMessage }: FieldProps): JSX.Element {
  const controlId = useId();
  const messageId = `${controlId}-msg`;
  const invalid = Boolean(error);

  return (
    <FieldContext.Provider value={{ controlId, describedBy: error || description ? messageId : undefined, invalid }}>
      {/* `data-required` is what paints the control yellow — see the
          `[data-required='true']` rule in `design-tokens.css`.

          Done as one CSS rule on the SHELL rather than a prop on each
          control because the shell is the only place that already knows
          whether the field is required, and the controls inside it are of
          five different kinds (input, select, textarea, the date group,
          the combobox). Threading a `required` prop through all five —
          two of which take no `Field` context today — would be five
          chances to miss one and ship a mandatory field that looks
          optional. */}
      <div data-required={required || undefined} className={cn('tw:flex tw:min-w-0 tw:flex-col tw:gap-0.5', className)}>
        <label
          htmlFor={controlId}
          className={cn(
            // 12px, not 10px, and normal tracking rather than `tracking-wider`.
            // Uppercase already costs legibility by flattening word shape;
            // widened tracking on top of a 10px glyph is what made these
            // unreadable from a working chair. The old value was `tw:text-label`.
            'tw:text-xs tw:uppercase',
            // A mandatory field's heading is bold and full-contrast, an
            // optional one's is semibold and muted. Optional labels used
            // `tw:text-nx-text-faint` (#80868b), which is 3.68:1 and fails
            // WCAG AA 1.4.3 for text this size; `text-muted` is 6.05:1.
            // The weight difference still carries the required/optional
            // distinction, so nothing is lost by raising the contrast.
            required ? 'tw:font-bold tw:text-nx-text' : 'tw:font-semibold tw:text-nx-text-muted',
            labelHidden && 'tw:sr-only',
          )}
        >
          {label}
          {required && (
            <span className="tw:ml-0.5 tw:text-nx-error" aria-hidden>
              *
            </span>
          )}
          {required && <span className="tw:sr-only"> (required)</span>}
        </label>
        {children}
        {error ? (
          <p id={messageId} role="alert" className="tw:flex tw:items-center tw:gap-1 tw:text-xs tw:text-nx-error">
            <Icon name="error" size={13} />
            {error}
          </p>
        ) : description && !hideMessage ? (
          <p id={messageId} className="tw:text-xs tw:text-nx-text-muted">
            {description}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

/** Lets a control inherit its id/aria wiring from the surrounding `Field`, or stand alone if there isn't one. */
function useFieldWiring(explicitInvalid?: boolean): { id?: string; 'aria-describedby'?: string; 'aria-invalid'?: boolean } {
  const context = useContext(FieldContext);
  const invalid = explicitInvalid ?? context?.invalid ?? false;
  return {
    id: context?.controlId,
    'aria-describedby': context?.describedBy,
    'aria-invalid': invalid || undefined,
  };
}

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  invalid?: boolean;
  /** Leading Material Symbol — decorative; the label carries the meaning. */
  icon?: Parameters<typeof Icon>[0]['name'];
}

/**
 * Browser autofill is OFF by default on every control in this system.
 *
 * The browser matches its saved profile by field name and type, so a
 * registration form full of `given_name` / `mobile_number` / `email` /
 * `address_line1` inputs gets the CLERK's own saved details offered on
 * every patient — a suggestion dropdown covering the field below, and a
 * one-keystroke path to writing the wrong person's data into a clinical
 * record. A shared front-desk workstation makes both worse.
 *
 * A caller that genuinely wants autofill (a staff-facing "my profile"
 * screen) passes `autoComplete` explicitly and this default gets out of
 * the way.
 */
const NO_AUTOFILL = 'off' as const;

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { invalid, icon, className, autoComplete = NO_AUTOFILL, ...props },
  ref,
) {
  const wiring = useFieldWiring(invalid);
  if (!icon)
    return (
      <input ref={ref} autoComplete={autoComplete} className={controlClass(Boolean(wiring['aria-invalid']), className)} {...wiring} {...props} />
    );
  return (
    <div className="tw:relative tw:min-w-0">
      <span aria-hidden className="tw:pointer-events-none tw:absolute tw:left-2 tw:top-1/2 tw:-translate-y-1/2 tw:text-nx-text-faint">
        <Icon name={icon} size={14} />
      </span>
      <input
        ref={ref}
        autoComplete={autoComplete}
        className={controlClass(Boolean(wiring['aria-invalid']), cn('tw:pl-7', className))}
        {...wiring}
        {...props}
      />
    </div>
  );
});

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean }>(
  function Textarea({ invalid, className, rows = 3, autoComplete = NO_AUTOFILL, ...props }, ref) {
    const wiring = useFieldWiring(invalid);
    return (
      <textarea
        ref={ref}
        rows={rows}
        autoComplete={autoComplete}
        className={controlClass(Boolean(wiring['aria-invalid']), cn('tw:h-auto tw:py-1.5 tw:leading-relaxed', className))}
        {...wiring}
        {...props}
      />
    );
  },
);

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  options: SelectOption[];
  /** Rendered as the first, empty option. Omit to make the select non-clearable. */
  placeholder?: string;
  /**
   * The placeholder is only the LABEL of an empty select, not a choice (desk, 2026-10-22: "need only Growth or Moderate growth
   * now there is a three option why?"). Hidden and disabled, so it reads as the label while nothing is picked and is absent from
   * the open list. Off by default: every other select keeps its pickable empty option.
   */
  placeholderHidden?: boolean;
  invalid?: boolean;
}

/**
 * A native `<select>` on purpose. For a bounded list it is faster to
 * operate (type-ahead, keyboard, native mobile wheel), fully accessible
 * with no ARIA of our own, and cannot trap focus — all things a custom
 * listbox has to re-earn. `Combobox` exists separately for the cases
 * that genuinely need search.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { options, placeholder, placeholderHidden, invalid, className, autoComplete = NO_AUTOFILL, ...props },
  ref,
) {
  const wiring = useFieldWiring(invalid);
  return (
    <select
      ref={ref}
      autoComplete={autoComplete}
      className={controlClass(Boolean(wiring['aria-invalid']), cn('tw:cursor-pointer tw:pr-6', className))}
      {...wiring}
      {...props}
    >
      {placeholder !== undefined && (
        <option value="" hidden={placeholderHidden} disabled={placeholderHidden}>
          {placeholder}
        </option>
      )}
      {options.map((option) => (
        <option key={option.value} value={option.value} disabled={option.disabled}>
          {option.label}
        </option>
      ))}
    </select>
  );
});

/** Checkbox and its label as one unit — the pair MUI needed `FormControlLabel` to assemble. */
export const Checkbox = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label: ReactNode }>(
  function Checkbox({ label, className, disabled, ...props }, ref) {
    return (
      <label
        className={cn(
          'tw:inline-flex tw:min-w-0 tw:items-center tw:gap-1.5 tw:text-xs tw:text-nx-text',
          disabled ? 'tw:cursor-not-allowed tw:opacity-50' : 'tw:cursor-pointer',
          className,
        )}
      >
        <input
          ref={ref}
          type="checkbox"
          disabled={disabled}
          className="nx-focusable tw:size-3.5 tw:shrink-0 tw:cursor-pointer tw:rounded-sm tw:border tw:border-nx-border-strong tw:accent-[var(--nx-primary)] tw:disabled:cursor-not-allowed"
          {...props}
        />
        <span className="tw:min-w-0">{label}</span>
      </label>
    );
  },
);

export const Radio = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label: ReactNode }>(
  function Radio({ label, className, disabled, ...props }, ref) {
    return (
      <label
        className={cn(
          'tw:inline-flex tw:min-w-0 tw:items-center tw:gap-1.5 tw:text-xs tw:text-nx-text',
          disabled ? 'tw:cursor-not-allowed tw:opacity-50' : 'tw:cursor-pointer',
          className,
        )}
      >
        <input
          ref={ref}
          type="radio"
          disabled={disabled}
          className="nx-focusable tw:size-3.5 tw:shrink-0 tw:cursor-pointer tw:accent-[var(--nx-primary)]"
          {...props}
        />
        <span className="tw:min-w-0">{label}</span>
      </label>
    );
  },
);

/**
 * Switch. A real `<input type="checkbox" role="switch">` rather than a
 * div with click handlers, so keyboard, form participation and
 * assistive-tech state come from the platform.
 */
export const Switch = forwardRef<HTMLInputElement, Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label: ReactNode }>(
  function Switch({ label, className, disabled, checked, ...props }, ref) {
    return (
      <label
        className={cn(
          'tw:inline-flex tw:min-w-0 tw:items-center tw:gap-2 tw:text-xs tw:text-nx-text',
          disabled ? 'tw:cursor-not-allowed tw:opacity-50' : 'tw:cursor-pointer',
          className,
        )}
      >
        <span className="tw:relative tw:inline-flex tw:shrink-0">
          <input
            ref={ref}
            type="checkbox"
            role="switch"
            checked={checked}
            disabled={disabled}
            className="nx-focusable tw:peer tw:size-0 tw:opacity-0"
            {...props}
          />
          <span
            aria-hidden
            className={cn(
              'tw:block tw:h-4 tw:w-7 tw:rounded-full tw:transition-colors tw:duration-200',
              checked ? 'tw:bg-nx-primary' : 'tw:bg-nx-border-strong',
            )}
          />
          <span
            aria-hidden
            className={cn(
              'tw:absolute tw:top-0.5 tw:size-3 tw:rounded-full tw:bg-white tw:transition-all tw:duration-200',
              checked ? 'tw:left-3.5' : 'tw:left-0.5',
            )}
          />
        </span>
        <span className="tw:min-w-0">{label}</span>
      </label>
    );
  },
);
